import React from 'react';
import { Helmet } from 'react-helmet-async';

const SEO = ({ 
  title, 
  description, 
  canonical, 
  type = 'website',
  structuredData = null,
  breadcrumbs = null,
  image = null,
  author = null,
  datePublished = null,
  dateModified = null
}) => {
  const siteUrl = process.env.REACT_APP_BACKEND_URL?.replace('/api', '') || 'https://orboroi.com';
  const fullUrl = canonical ? `${siteUrl}${canonical}` : siteUrl;
  const defaultImage = `${siteUrl}/og-image.jpg`;
  const ogImage = image || defaultImage;
  
  // Use custom titles for homepage, otherwise append " | Orboroi"
  const isHomepage = !canonical || canonical === '/';
  const pageTitle = isHomepage ? 'Orboroi — Data Certainty for AI' : `${title} | Orboroi`;
  const ogTitle = isHomepage ? 'Orboroi — Data Certainty for AI' : `${title} | Orboroi`;
  const ogDescription = isHomepage ? 'Achieve Data Certainty Across the Entire AI Model Lifecycle.' : description;

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{pageTitle}</title>
      <meta name="title" content={pageTitle} />
      <meta name="description" content={description} />
      <link rel="canonical" href={fullUrl} />

      {/* Open Graph / Facebook / LinkedIn */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={fullUrl} />
      <meta property="og:title" content={ogTitle} />
      <meta property="og:description" content={ogDescription} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:site_name" content="Orboroi" />
      <meta property="og:locale" content="en_US" />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={fullUrl} />
      <meta name="twitter:title" content={ogTitle} />
      <meta name="twitter:description" content={ogDescription} />
      <meta name="twitter:image" content={ogImage} />
      <meta name="twitter:image:alt" content={pageTitle} />

      {/* Article specific meta tags */}
      {type === 'article' && author && (
        <meta name="author" content={author} />
      )}
      {type === 'article' && datePublished && (
        <meta property="article:published_time" content={datePublished} />
      )}
      {type === 'article' && dateModified && (
        <meta property="article:modified_time" content={dateModified} />
      )}

      {/* Structured Data - Organization & WebSite (always present) */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Organization",
          "name": "Orboroi",
          "url": siteUrl,
          "logo": `${siteUrl}/logo.png`,
          "description": "AI Data Operations - Annotation, Validation, and Model Evaluation Services",
          "contactPoint": {
            "@type": "ContactPoint",
            "email": "contact@orboroi.com",
            "contactType": "Customer Service"
          }
        })}
      </script>

      {/* Breadcrumb Structured Data */}
      {breadcrumbs && (
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": breadcrumbs.map((crumb, index) => ({
              "@type": "ListItem",
              "position": index + 1,
              "name": crumb.name,
              "item": `${siteUrl}${crumb.url}`
            }))
          })}
        </script>
      )}

      {/* Custom Structured Data (Article, Product, Service, etc.) */}
      {structuredData && (
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      )}
    </Helmet>
  );
};

export default SEO;
